import { PlacementRuleType } from '../../../api/generated';
import { hasFirstRule, hasLastRule } from './layout';
import type { EditorDependency, EditorNode } from './types';

// A FIRST/LAST anchor is allowed only once per group. True when another
// intervention already holds the requested anchor.
export function isAnchorTakenElsewhere(
  allNodes: EditorNode[],
  sourceId: string,
  type: PlacementRuleType
): boolean {
  if (type !== PlacementRuleType.First && type !== PlacementRuleType.Last) return false;
  return allNodes.some(
    (n) => n.id !== sourceId && n.dependencies.some((d) => d.type === type)
  );
}

// Rule types that should be disabled in the add-constraint dropdown for a node:
// its own conflicts plus a FIRST/LAST already used by another intervention.
export function disabledDepTypes(
  allNodes: EditorNode[],
  sourceId: string
): Set<PlacementRuleType> {
  const existing = allNodes.find((n) => n.id === sourceId)?.dependencies ?? [];
  const disabled = new Set<PlacementRuleType>();
  for (const type of Object.values(PlacementRuleType)) {
    if (isDepTypeDisabled(existing, type) || isAnchorTakenElsewhere(allNodes, sourceId, type)) {
      disabled.add(type);
    }
  }
  return disabled;
}

export function isDepTypeDisabled(existing: EditorDependency[], type: PlacementRuleType): boolean {
  const hasFirst = existing.some((d) => d.type === PlacementRuleType.First);
  const hasLast = existing.some((d) => d.type === PlacementRuleType.Last);
  if (type === PlacementRuleType.First)
    return existing.some(
      (d) =>
        d.type === PlacementRuleType.First ||
        d.type === PlacementRuleType.After ||
        d.type === PlacementRuleType.ImmediatelyAfter
    );
  if (type === PlacementRuleType.Last)
    return existing.some(
      (d) =>
        d.type === PlacementRuleType.Last ||
        d.type === PlacementRuleType.Before ||
        d.type === PlacementRuleType.ImmediatelyBefore
    );
  if (type === PlacementRuleType.After || type === PlacementRuleType.ImmediatelyAfter)
    return hasFirst;
  if (type === PlacementRuleType.Before || type === PlacementRuleType.ImmediatelyBefore)
    return hasLast;
  return false;
}

const isAfterRule = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.After || type === PlacementRuleType.ImmediatelyAfter;

const isBeforeRule = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.Before || type === PlacementRuleType.ImmediatelyBefore;

// Adjacency map of the ordering graph: an edge X → Y means "X must be
// placed before Y." BEFORE on X targeting Y produces X → Y; AFTER on X
// targeting Y produces Y → X. FIRST/LAST anchors don't pin pairwise order.
function buildOrderingGraph(nodes: EditorNode[]): Map<string, Set<string>> {
  const idSet = new Set(nodes.map((n) => n.id));
  const adj = new Map<string, Set<string>>();
  for (const n of nodes) adj.set(n.id, new Set());
  for (const n of nodes) {
    for (const dep of n.dependencies) {
      if (!dep.targetId || !idSet.has(dep.targetId)) continue;
      if (isBeforeRule(dep.type)) adj.get(n.id)?.add(dep.targetId);
      else if (isAfterRule(dep.type)) adj.get(dep.targetId)?.add(n.id);
    }
  }
  return adj;
}

function hasPath(
  adj: Map<string, Set<string>>,
  from: string,
  to: string
): boolean {
  if (from === to) return true;
  const visited = new Set<string>([from]);
  const queue = [from];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    for (const w of adj.get(current) ?? []) {
      if (w === to) return true;
      if (!visited.has(w)) {
        visited.add(w);
        queue.push(w);
      }
    }
  }
  return false;
}

// True if the ordering graph already contains a cycle (a contradiction such as
// A-before-B and B-before-A). Used as a final guard so no path — typing or
// dragging — can leave the plan with a loop.
export function hasCycle(nodes: EditorNode[]): boolean {
  const adj = buildOrderingGraph(nodes);
  const state = new Map<string, 'visiting' | 'done'>();
  const walk = (u: string): boolean => {
    state.set(u, 'visiting');
    for (const v of adj.get(u) ?? []) {
      const childState = state.get(v);
      if (childState === 'visiting') return true;
      if (childState === undefined && walk(v)) return true;
    }
    state.set(u, 'done');
    return false;
  };
  return nodes.some((n) => state.get(n.id) === undefined && walk(n.id));
}

// True when any rule contradicts a FIRST/LAST anchor: an after-rule targeting
// a LAST-anchored node (or held by a FIRST-anchored node), or a before-rule
// targeting a FIRST-anchored node (or held by a LAST-anchored node). Note that
// the inverse pairs are legal: "after the FIRST node" and "before the LAST
// node" are ordinary chain links. Used as a final guard alongside hasCycle so
// no path — typing or dragging — can place a block after LAST / before FIRST.
export function hasAnchorViolation(nodes: EditorNode[]): boolean {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return nodes.some((n) => {
    const holdsFirst = hasFirstRule(n);
    const holdsLast = hasLastRule(n);
    return n.dependencies.some((d) => {
      const target = d.targetId ? byId.get(d.targetId) : undefined;
      if (isAfterRule(d.type)) return holdsFirst || (target !== undefined && hasLastRule(target));
      if (isBeforeRule(d.type)) return holdsLast || (target !== undefined && hasFirstRule(target));
      return false;
    });
  });
}

// Adding edge u → v creates a cycle iff there's already a path from v to u.
function wouldCreateCycle(
  nodes: EditorNode[],
  sourceId: string,
  type: PlacementRuleType,
  targetId: string
): boolean {
  if (sourceId === targetId) return true;
  const adj = buildOrderingGraph(nodes);
  if (isBeforeRule(type)) return hasPath(adj, targetId, sourceId);
  if (isAfterRule(type)) return hasPath(adj, sourceId, targetId);
  return false;
}

// Error key for a FIRST/LAST anchor already held by another intervention.
function anchorConflictError(type: PlacementRuleType): string {
  return type === PlacementRuleType.First
    ? 'config-dep-err-first-conflict'
    : 'config-dep-err-last-conflict';
}

// Error key explaining why a rule type is disabled for the source node.
function disabledTypeError(type: PlacementRuleType): string | null {
  if (type === PlacementRuleType.First) return 'config-dep-err-first-conflict';
  if (type === PlacementRuleType.Last) return 'config-dep-err-last-conflict';
  if (type === PlacementRuleType.After || type === PlacementRuleType.ImmediatelyAfter)
    return 'config-dep-err-first-blocks-after';
  if (type === PlacementRuleType.Before || type === PlacementRuleType.ImmediatelyBefore)
    return 'config-dep-err-last-blocks-before';
  return null;
}

// Error key when the new rule would place the source relative to an anchor's
// forbidden side: after a LAST-anchored target or before a FIRST-anchored one.
function targetAnchorError(
  nodes: EditorNode[],
  type: PlacementRuleType,
  targetId: string
): string | null {
  const target = nodes.find((n) => n.id === targetId);
  if (!target) return null;
  if (isAfterRule(type) && hasLastRule(target)) return 'config-dep-err-target-last';
  if (isBeforeRule(type) && hasFirstRule(target)) return 'config-dep-err-target-first';
  return null;
}

// Error key when the new rule contradicts or duplicates a rule the source
// already has against the same target.
function targetConflictError(
  existing: EditorDependency[],
  type: PlacementRuleType,
  targetId: string
): string | null {
  const same = existing.filter((d) => d.targetId === targetId);
  if (
    (type === PlacementRuleType.First || type === PlacementRuleType.ImmediatelyAfter) &&
    same.some(
      (d) => d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter
    )
  )
    return 'config-dep-err-already-after';
  if (
    (type === PlacementRuleType.Before || type === PlacementRuleType.ImmediatelyBefore) &&
    same.some(
      (d) => d.type === PlacementRuleType.Before || d.type === PlacementRuleType.ImmediatelyBefore
    )
  )
    return 'config-dep-err-already-before';
  if (type === PlacementRuleType.First && same.some((d) => d.type === PlacementRuleType.Before))
    return 'config-dep-err-after-before-conflict';
  if (type === PlacementRuleType.Before && same.some((d) => d.type === PlacementRuleType.After))
    return 'config-dep-err-after-before-conflict';
  return null;
}

/** Returns a translation key, or null if valid. Caller translates with t(). */
export function validateNewDep(
  nodes: EditorNode[],
  sourceId: string,
  type: PlacementRuleType,
  targetId?: string
): string | null {
  const source = nodes.find((n) => n.id === sourceId);
  const existing = source?.dependencies ?? [];
  if (isAnchorTakenElsewhere(nodes, sourceId, type)) return anchorConflictError(type);
  if (isDepTypeDisabled(existing, type)) {
    const error = disabledTypeError(type);
    if (error) return error;
  }
  if (targetId) {
    const conflict = targetConflictError(existing, type, targetId);
    if (conflict) return conflict;
    const anchorError = targetAnchorError(nodes, type, targetId);
    if (anchorError) return anchorError;
    if (wouldCreateCycle(nodes, sourceId, type, targetId))
      return 'config-dep-err-would-create-cycle';
  }
  return null;
}
